import type { Except } from "type-fest";
import { z, type ZodSchema } from "zod";
import { type Color, colorZodSchema } from "../color";

type ColorSet = {
    _type: "COLOR_SET";
    asString: string;
    main: Color;
    light: Color;
    dark: Color;
    background: Color;
    border: Color;
    placeholder: Color;
    shadow: Color;
    text: Color;
    link: Color;
    accent?: Color;
    backgroundGradientAlt?: Color;
};

type ColorSetWithoutMeta = Except<ColorSet, "_type" | "asString">;
const colorSetWithoutMetaZodSchema: ZodSchema<ColorSetWithoutMeta> = z.object({
    main: colorZodSchema,
    light: colorZodSchema,
    dark: colorZodSchema,
    background: colorZodSchema,
    border: colorZodSchema,
    placeholder: colorZodSchema,
    shadow: colorZodSchema,
    text: colorZodSchema,
    link: colorZodSchema,
    accent: colorZodSchema.optional(),
    backgroundGradientAlt: colorZodSchema.optional(),
});

const colorSetZodSchema: ZodSchema<ColorSet> = z
    .object({ _type: z.literal("COLOR_SET"), asString: z.string() })
    .and(colorSetWithoutMetaZodSchema);

const colorSetFactory = (
    params: Except<ColorSetWithoutMeta, "link"> & {
        link?: Color;
    },
): ColorSet => {
    if (!params.main && !params.background)
        throw new Error("Either background or main color has to be provided");

    const main = params.main || params.background;
    const colorSetData = {
        _type: "COLOR_SET",
        ...params,
        main,
        background: params.background || params.main,
        link: params.link || params.text,
        asString: main.asString,
    } satisfies ColorSet;
    return colorSetData; // colorSetZodSchema.parse(colorSetData);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isColorSet = (value: any): value is ColorSet => {
    return value?._type === "COLOR_SET";
};

export {
    colorSetFactory,
    colorSetWithoutMetaZodSchema,
    colorSetZodSchema,
    isColorSet,
};
export type { ColorSet, ColorSetWithoutMeta };
